<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="User Management"
      subtitle="Manage system users and access control"
      icon="fas fa-users"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableUsers.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Role"
          label-for="filterRole"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableUsers.selected.role"
            placeholder="filter role here"
            input-id="filterRole"
            label="role_name"
            clearable
            append-to-body
            :options="filterRolesByActorHierarchy(currentUser, tableUsers.options.roles)"
            :loading="tableUsers.fetching.roles"
            :disabled="tableUsers.busy || tableUsers.fetching.roles"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ role_name }">
              <div
                class="text-break text-wrap"
                v-text="role_name"
              />
            </template>
            <template #selected-option="{ role_name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="role_name"
              />
            </template>
            <template #no-options="">
              no available role
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol cols="12">
        <BButton
          size="sm"
          type="button"
          variant="primary"
          @click.prevent="createUser"
        >
          Create User
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableUsers"
      :provider="tableProvider"
      :fields="tableUsers.fields"
      :filter="tableUsers.filter"
      :filter-on="tableUsers.filterOn"
      :sort-by="tableUsers.sortBy"
      :sort-desc="tableUsers.sortDesc"
      :sort-direction="tableUsers.sortDirection"
      :total-rows="tableUsers.totalRows"
      :current-page="tableUsers.currentPage"
      :per-page="tableUsers.perPage"
      :stacked="tableUsers.stacked"
      :busy="tableUsers.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableUsers.busy"
            @click.prevent="editUser(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableUsers.busy"
            @click.prevent="deleteUserConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(role)="{ item }">
        <BBadge :variant="getRoleVariant(item.role?.role_name)">
          {{ item.role?.role_name || 'N/A' }}
        </BBadge>
      </template>

      <template #cell(active)="{ item }">
        <BBadge :variant="item.active ? 'success' : 'secondary'">
          {{ item.active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalUser"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MUserTitle"
    >
      <ValidationObserver ref="formUser">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="first_name"
                name="first name"
                rules="required|max:50"
              >
                <BFormGroup
                  label="First Name"
                  label-for="fFirstName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fFirstName"
                    v-model="formUser.first_name"
                    placeholder="enter first name here"
                    maxlength="50"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="last_name"
                name="last name"
                rules="required|max:50"
              >
                <BFormGroup
                  label="Last Name"
                  label-for="fLastName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fLastName"
                    v-model="formUser.last_name"
                    placeholder="enter last name here"
                    maxlength="50"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="username"
                name="username"
                rules="required|max:50"
              >
                <BFormGroup
                  label="Username"
                  label-for="fUsername"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fUsername"
                    v-model="formUser.username"
                    placeholder="enter username here"
                    maxlength="50"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="email"
                name="email"
                rules="required|email"
              >
                <BFormGroup
                  label="Email"
                  label-for="fEmail"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEmail"
                    v-model="formUser.email"
                    placeholder="enter email here"
                    autocomplete="off"
                    type="email"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="mobile_number"
                name="mobile number"
                rules="min:11|max:11"
              >
                <BFormGroup
                  label="Mobile Number"
                  label-for="fMobileNumber"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fMobileNumber"
                    v-model="formUser.mobile_number"
                    placeholder="enter mobile number here"
                    maxlength="11"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="role_id"
                name="role"
                rules="required"
              >
                <BFormGroup
                  label="Role"
                  label-for="fRole"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.role"
                    placeholder="select role here"
                    input-id="fRole"
                    label="role_name"
                    append-to-body
                    :options="filterRolesByActorHierarchy(currentUser, options.roles)"
                    :loading="fetching.roles"
                    :disabled="state.busy || fetching.roles"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ role_name }">
                      <div
                        class="text-break text-wrap"
                        v-text="role_name"
                      />
                    </template>
                    <template #selected-option="{ role_name }">
                      <div
                        class="text-truncate"
                        v-text="role_name"
                      />
                    </template>
                    <template #no-options="">
                      no available roles
                    </template>
                  </VSelect>
                  <small
                    v-if="!state.editing && currentRoleCode === 'admin'"
                    class="form-text text-muted"
                  >
                    Only superuser can create admin users
                  </small>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="password"
                name="password"
                rules="min:8"
              >
                <BFormGroup
                  label="New Password (Optional)"
                  label-for="fPassword"
                  label-class="font-weight-bolder"
                  description="Leave blank to keep current password"
                  class="mb-2"
                >
                  <BFormInput
                    id="fPassword"
                    v-model="formUser.password"
                    placeholder="enter new password here (optional)"
                    autocomplete="off"
                    maxlength="50"
                    type="password"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formUser.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateUser"
        >
          {{ state.editing ? 'Update' : 'Create' }} User
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRoles from '@/mixins/shared/roles'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminUser } from '@/services'

export default {
  name: 'VAdminUsers',

  components: {
    AdminPageHeader
  },

  mixins: [
    MRoles,
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Users'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      selected: {
        role: ''
      },
      options: {
        roles: []
      },
      fetching: {
        roles: false
      },
      formUser: {
        id: '',
        role_id: '',
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        password: '',
        active: 1
      },
      tableUsers: {
        busy: false,
        fetching: {
          roles: false
        },
        options: {
          roles: []
        },
        selected: {
          role: ''
        },
        filter: {
          search: '',
          role: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 2, key: 'username', class: 'min-w-150px' },
          { mobile: 3, key: 'first_name', label: 'Name', class: 'min-w-150px', formatter: this.getFullName },
          { mobile: 4, key: 'mobile_number', class: 'text-md-right min-w-150px' },
          { mobile: 5, key: 'email', class: 'min-w-md-175px' },
          { mobile: 9, key: 'role.role_name', label: 'Role' },
          { mobile: 10, key: 'active', class: 'text-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 11, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 12, key: 'last_login', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MUserTitle () {
      return this.state.editing ? 'Edit User' : 'Add User'
    },

    currentRoleCode () {
      if (this.currentUser?.role?.role_code) {
        return this.currentUser.role.role_code
      }

      return this.currentUser?.role || ''
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.role' (role) {
      this.formUser.role_id = role?.id || ''
    },

    'tableUsers.selected.role' (role) {
      this.tableUsers.filter.role = role?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getRoles(this.tableUsers)
    }
  },

  methods: {
    getRoleVariant (role) {
      const variants = {
        Admin: 'danger',
        Staff: 'info',
        Student: 'success'
      }
      return variants[role] || 'secondary'
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableUsers.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        role: filter.role
      }

      return await SAdminUser.getList(payload).then(
        ({ data }) => {
          this.tableUsers.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableUsers.totalRows = 0
        return []
      }).finally(() => {
        this.tableUsers.busy = false
      })
    },

    createUser () {
      this.state.editing = false

      this.formUser = Object.assign(this.formUser, {
        id: '',
        role_id: '',
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        password: '',
        active: 1
      })

      this.selected.role = ''

      this.getRoles(this)

      this.$refs.modalUser.show()
    },

    editUser (user) {
      this.state.editing = true

      this.formUser = Object.assign(this.formUser, {
        id: user.id,
        role_id: user.role_id,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_number: user.mobile_number,
        password: '',
        active: user.active
      })

      this.getRoles(this).finally(() => {
        this.selected.role = QUERY.find(this.options.roles, {
          id: Number(user?.role_id)
        }) || ''
      })

      this.$refs.modalUser.show()
    },

    async validateUser () {
      await this.$refs.formUser.validate().then(
        async validUser => {
          if (!validUser) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this User?</h6>' : '<h6>Create this User?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putUser() : this.postUser()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formUser.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postUser () {
      return await SAdminUser.store(this.formUser).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalUser.hide()
            this.$refs.tableUsers.refetch()
          })
        }
      )
    },

    async putUser () {
      return await SAdminUser.update(this.formUser.id, this.formUser).then(
        ({ data: { message, user } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalUser.hide()
            this.$refs.tableUsers.updateRow({
              id: user.id,
              username: user.username,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              mobile_number: user.mobile_number,
              role: user.role,
              role_id: user.role_id,
              active: user.active,
              updated_at: user.updated_at
            })
          })
        }
      )
    },

    async deleteUserConfirm (user) {
      const result = await this.$swal({
        title: 'Delete User',
        text: `Are you sure you want to delete ${user.first_name} ${user.last_name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.deleteUser(user.id)
      }
    },

    async deleteUser (id) {
      this.state.busy = true
      try {
        await SAdminUser.destroy(id)
        this.swalSuccess('User deleted successfully')
        this.$refs.tableUsers.refetch()
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to delete user'
        this.swalError(message)
      } finally {
        this.state.busy = false
      }
    },

    /**
     * Filter roles based on actor's role hierarchy
     * Superuser: can create users with any roles
     * Admin: can create all roles except admin
     * Others: see no roles
     */
    filterRolesByActorHierarchy (actor, roles) {
      if (!Array.isArray(roles)) {
        return []
      }

      const roleCode = actor?.role?.role_code || actor?.role

      if (!roleCode) {
        return []
      }

      if (roleCode === 'superuser') {
        return roles
      } else if (roleCode === 'admin') {
        return roles.filter(role => role.role_code !== 'admin' && role.role_code !== 'superuser')
      }

      return []
    }
  }
}
</script>
